import { useEffect, useState } from 'react';
import {
  type RentalManagerAuthCheckUserInfoDto,
  RentalManagerUserInfoV2WebApiHandler_get,
  type UserInfoRestrictionsDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import { Button, Heading, ModalDialog, Paragraph } from '@zillow/constellation';
import { PartialMessage } from '@zg-rentals/backend-styled-components';
import { analyticsUtils } from '@zg-rentals/analytics';
import ClickstreamEvents from './clickstream/ClickstreamEvents';

type Props = {
  restrictions: UserInfoRestrictionsDto;
};

export const BlockedUserModal = ({ restrictions: { partialMessages, title, ctaUrl, ctaText } }: Props) => {
  const [userData, setUserData] = useState<RentalManagerAuthCheckUserInfoDto | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      const data = await RentalManagerUserInfoV2WebApiHandler_get({});
      setUserData(data);
    };
    fetchUser();
  }, []);

  useEffect(() => {
    if (userData) {
      analyticsUtils.trackUAEvent(ClickstreamEvents.ViewBlockedUserModal({ user: userData }));
    }
  }, [userData]);

  const handleClose = () => {
    if (userData) {
      analyticsUtils.trackUAEvent(ClickstreamEvents.ClickReturnToZillow({ user: userData }));
    }
  };

  const handleUrlClick = (e: React.MouseEvent<HTMLAnchorElement>, url: string, text: string) => {
    if (userData) {
      const triggerObject = text
        .toLowerCase()
        .replace(
          /\s+/g,
          '_',
        ) as (typeof ClickstreamEvents.BLOCKED_USER_MODAL_TRIGGER_OBJECTS)[keyof typeof ClickstreamEvents.BLOCKED_USER_MODAL_TRIGGER_OBJECTS];
      analyticsUtils.trackUAEvent(
        ClickstreamEvents.ClickContactSupport({
          user: userData,
          triggerObject,
        }),
      );
    }
  };

  return (
    <ModalDialog
      renderHeader={
        <Heading as="h1" level={6}>
          Alert
        </Heading>
      }
      renderBody={
        <>
          <Heading fontFamily="serif" marginBottom="sm" level={4} as="h2">
            {title}
          </Heading>

          <Paragraph>
            <PartialMessage partialMessages={partialMessages} onUrlClick={handleUrlClick} />
          </Paragraph>
        </>
      }
      renderFooter={
        <Button as="a" href={ctaUrl} buttonType="primary" onClick={handleClose}>
          {ctaText}
        </Button>
      }
      isOpen={true}
      shouldCloseOnESCKeyPress={false}
      shouldCloseOnOutsideClick={false}
      closeButton={null}
      // @ts-ignore
      size={{ default: ModalDialog.SIZES.FULL_SCREEN, md: ModalDialog.SIZES.MD }}
    />
  );
};

export default BlockedUserModal;
